// import node module libraries
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { v4 as uuid } from "uuid";
import { DashInnerItemProps } from "@/types";

interface InsightsProps {
  id: string;
  insight: string;
}

interface dashItemProps {
  dash_id: string;
  dash_name: string;
  dash_desc: string;
  dash_slug: string;
  dash_preview: string;
  dash_insights: InsightsProps[];
  dash_items: DashInnerItemProps[];
  last_updated?: string | any;
}

interface DashboardState {
  dashboards: dashItemProps[];
  selectedDashboardId: string | null;
  currentDashboard: dashItemProps;
  initialLayout: DashInnerItemProps[];
}

// currentDashboard: { [key: string]: dashItemProps | null };

const initialState: DashboardState = {
  dashboards: [],
  selectedDashboardId: null,
  currentDashboard: {
    dash_id: "",
    dash_name: "",
    dash_desc: "",
    dash_slug: "",
    dash_preview: "",
    dash_insights: [],
    dash_items: [],
    last_updated: "",
  },
  initialLayout: [],
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setCurrentDashboard: (state, action: PayloadAction<dashItemProps>) => {
      const { dash_items = [] } = action.payload || {};
      state.currentDashboard = action.payload;
      state.initialLayout = dash_items;
    },

    resetLayout: (state) => {
      state.currentDashboard.dash_items = state.initialLayout;
    },
    createDashboard: (
      state,
      action: PayloadAction<{
        dash_id: string;
        dash_name: string;
        dash_desc: string;
        dash_slug: string;
      }>
    ) => {
      const { dash_id, dash_name, dash_desc, dash_slug } = action.payload;

      // Check for duplicates
      if (!state.dashboards.some((d) => d.dash_name === dash_name)) {
        const newDashboard: dashItemProps = {
          dash_id,
          dash_name,
          dash_desc,
          dash_slug,
          dash_preview: "",
          dash_items: [],
          dash_insights: [],
        };

        state.dashboards.push(newDashboard);
      } else {
        console.warn("Duplicate dashboard name:", dash_name);
      }
    },

    // New action to update dashboard title and description

    selectDashboard: (state, action: PayloadAction<string>) => {
      state.selectedDashboardId = action.payload;
    },
    addItem: (
      state,
      action: PayloadAction<{
        dash_items: any;
      }>
    ) => {
      const { dash_items } = action.payload;

      const TOTAL_COLUMNS = 3;
      const ITEM_WIDTH = 4;
      const ITEM_HEIGHT = 8;

      const Items = state.currentDashboard.dash_items;

      const numItems = Items.length;

      const rowIndex = Math.floor(numItems / TOTAL_COLUMNS);
      const colIndex = numItems % TOTAL_COLUMNS;
      const x = colIndex * ITEM_WIDTH;
      const y = rowIndex * ITEM_HEIGHT;

      state.currentDashboard.dash_items.push({
        i: dash_items.id,
        x,
        y,
        w: ITEM_WIDTH,
        h: ITEM_HEIGHT,
        ...dash_items,
      });
    },
    removeItem: (state, action: PayloadAction<string>) => {
      const selectedDashboard = state.dashboards.find(
        (dashboard) => dashboard.dash_id === state.selectedDashboardId
      );

      if (selectedDashboard) {
        selectedDashboard.dash_items = selectedDashboard.dash_items.filter(
          (item: any) => item.id !== action.payload
        );
      }
    },
    removeCurrentDashItem: (state, action: PayloadAction<string>) => {
      state.currentDashboard.dash_items =
        state.currentDashboard.dash_items.filter(
          (item: any) => item.id !== action.payload
        );
    },

    setLayout: (state, action: PayloadAction<any>) => {
      const selectedDashboardIndex = state.dashboards.findIndex(
        (dashboard) => dashboard.dash_id === state.selectedDashboardId
      );

      if (selectedDashboardIndex !== -1) {
        const selectedDashboard = state.dashboards[selectedDashboardIndex];

        const updatedLayout = action.payload.map((newItem: any) => {
          const existingItem = selectedDashboard.dash_items.find(
            (item: any) => item.i === newItem.i
          );
          return existingItem ? { ...existingItem, ...newItem } : newItem;
        });

        // Create a new dashboard object with updated items
        const updatedDashboard = {
          ...selectedDashboard,
          dash_items: updatedLayout,
        };

        // Update the dashboards array with the new dashboard object
        state.dashboards[selectedDashboardIndex] = updatedDashboard;
      } else {
        console.warn("No selected dashboard found to update layout.");
      }
    },
    setCurrentDashboardLayout: (state, action: PayloadAction<any>) => {
      const updatedLayout = action.payload.map((newItem: any) => {
        const existingItem = state.currentDashboard.dash_items.find(
          (item: any) => item.i === newItem.i
        );
        return existingItem ? { ...existingItem, ...newItem } : newItem;
      });

      // Create a new dashboard object with updated items
      const updatedDashboard = {
        ...state.currentDashboard,
        dash_items: updatedLayout,
      };

      state.currentDashboard = updatedDashboard;
    },
    setDashboards: (state, action: PayloadAction<dashItemProps[]>) => {
      state.dashboards = action.payload;
    },
    deleteDashboard: (state, action: PayloadAction<string>) => {
      state.dashboards = state.dashboards.filter(
        (dashboard) => dashboard.dash_id !== action.payload
      );
    },
    updateDashboardTitle: (
      state,
      action: PayloadAction<{
        dash_id: string;
        dash_name: string;
        dash_desc: string;
      }>
    ) => {
      const { dash_id, dash_name, dash_desc } = action.payload;

      state.currentDashboard.dash_name = dash_name;
      state.currentDashboard.dash_desc = dash_desc;
    },
    updateDashItemTitle: (
      state,
      action: PayloadAction<{ id: string; title: string }>
    ) => {
      const { id, title } = action.payload;
      const item = state.currentDashboard.dash_items.find(
        (item: any) => item.id === id
      );
      if (item) {
        item.title = title;
      }
    },

    updateWidgetContent: (
      state,
      action: PayloadAction<{
        id: string;
        content: Partial<DashInnerItemProps>;
      }>
    ) => {
      const { id, content } = action.payload;
      const item = state.currentDashboard.dash_items.find(
        (item) => item.id === id
      );
      if (item) {
        Object.assign(item, content);
      }
    },
    updateHeading: (
      state,
      action: PayloadAction<{ id: string; heading: string }>
    ) => {
      const { id, heading } = action.payload;
      const item = state.currentDashboard.dash_items.find(
        (item: any) => item.id === id
      );
      if (item) {
        item.heading = heading;
      }
    },
    updateDescription: (
      state,
      action: PayloadAction<{ id: string; description: string }>
    ) => {
      const { id, description } = action.payload;

      const item = state.currentDashboard.dash_items.find(
        (item: any) => item.id === id
      );
      if (item) {
        item.description = description;
      }
    },

    setInitialInsights: (
      state,
      action: PayloadAction<{ insights: InsightsProps[] }>
    ) => {
      const { insights } = action.payload;

      state.currentDashboard.dash_insights = insights;
    },
    updateDashInsights: (
      state,
      action: PayloadAction<{ id: string; insight: string }>
    ) => {
      const { id, insight } = action.payload;

      if (id) {
        // Update existing insight
        const insightIndex = state.currentDashboard.dash_insights.findIndex(
          (ins) => ins.id === id
        );
        if (insightIndex !== -1) {
          state.currentDashboard.dash_insights[insightIndex] = { id, insight };
        }
      } else {
        // Add new insight
        state.currentDashboard.dash_insights.push({
          id: action.payload.id,
          insight: action.payload.insight,
        });
      }
    },
    addNewInsight: (state, action: PayloadAction<InsightsProps>) => {
      state.currentDashboard.dash_insights.push(action.payload);
    },
    removeDashInsight: (state, action: PayloadAction<string>) => {
      state.currentDashboard.dash_insights =
        state.currentDashboard.dash_insights.filter(
          (ins) => ins.id !== action.payload
        );
    },
    rephraseInsight: (
      state,
      action: PayloadAction<{ id: string; insight: string; dash_id: string }>
    ) => {
      const { id, insight } = action.payload;

      const insightIndex = state.currentDashboard.dash_insights.findIndex(
        (ins) => ins.id === id
      );

      if (insightIndex !== -1) {
        state.currentDashboard.dash_insights[insightIndex] = { id, insight };
      }
    },

    copyItem: (state, action: PayloadAction<string>) => {
      const newItemId = action.payload; // This should be the ID of the item to copy
      const selectedDashboard = state.dashboards.find(
        (dashboard) => dashboard.dash_id === state.selectedDashboardId
      );

      if (selectedDashboard) {
        // Change 'i' to 'id' if that's what you're using for the unique identifier
        const existingItem = selectedDashboard.dash_items.find(
          (item) => item.id === newItemId // Ensure you're using the correct identifier
        );

        if (existingItem) {
          const newId = uuid(); // Generate a new unique ID
          const totalColumns = 3; // Number of columns per row
          const itemWidth = 4; // Width of each item
          const itemHeight = 8; // Height of each item

          // Calculate the new item's position
          const numItems = selectedDashboard.dash_items.length;
          const rowIndex = Math.floor(numItems / totalColumns);
          const colIndex = numItems % totalColumns;
          const xPosition = colIndex * itemWidth;
          const yPosition = rowIndex * itemHeight;

          if (existingItem.type === "heading") {
            const newItem = {
              ...existingItem,
              id: newId,
              x: xPosition,
              i: newId,
              y: yPosition,
              heading: `${existingItem.heading} - Copy`,
            };

            // Push the new item to the dashboard items array
            selectedDashboard.dash_items.push(newItem);
          } else if (existingItem.type === "description") {
            const newItem = {
              ...existingItem,
              id: newId,
              x: xPosition,
              i: newId,
              y: yPosition,
              description: `${existingItem.description} - Copy`,
            };

            // Push the new item to the dashboard items array
            selectedDashboard.dash_items.push(newItem);
          } else {
            // Create a copy of the existing item, modifying the necessary fields
            const newItem: DashInnerItemProps = {
              ...existingItem,
              id: newId, // Assign new unique ID
              title: `${existingItem.title} - Copy`, // Modify the title to indicate it's a copy
              x: xPosition, // Set new position
              i: newId,
              y: yPosition,
            };

            // Push the new item to the dashboard items array
            selectedDashboard.dash_items.push(newItem);
          }
        } else {
          console.warn("Existing item not found:", newItemId);
        }
      } else {
        console.warn("Selected dashboard not found.");
      }
    },
    copyCurrentDashItem: (state, action: PayloadAction<string>) => {
      // This should be the ID of the item to copy
      const existingItem = state.currentDashboard.dash_items.find(
        (dashItem) => dashItem.id === action.payload
      );

      if (existingItem) {
        const newId = uuid(); // Generate a new unique ID
        const totalColumns = 3; // Number of columns per row
        const itemWidth = 4; // Width of each item
        const itemHeight = 8; // Height of each item

        // Calculate the new item's position
        const numItems = state.currentDashboard.dash_items.length;
        const rowIndex = Math.floor(numItems / totalColumns);
        const colIndex = numItems % totalColumns;
        const xPosition = colIndex * itemWidth;
        const yPosition = rowIndex * itemHeight;

        if (existingItem.type === "heading") {
          const newItem = {
            ...existingItem,
            id: newId,
            x: xPosition,
            i: newId,
            y: yPosition,
            heading: `${existingItem.heading} - Copy`,
          };

          // Push the new item to the dashboard items array
          state.currentDashboard.dash_items.push(newItem);
        } else if (existingItem.type === "description") {
          const newItem = {
            ...existingItem,
            id: newId,
            x: xPosition,
            i: newId,
            y: yPosition,
            description: `${existingItem.description} - Copy`,
          };

          // Push the new item to the dashboard items array
          state.currentDashboard.dash_items.push(newItem);
        } else {
          // Create a copy of the existing item, modifying the necessary fields
          const newItem: DashInnerItemProps = {
            ...existingItem,
            id: newId, // Assign new unique ID
            title: `${existingItem.title} - Copy`, // Modify the title to indicate it's a copy
            x: xPosition, // Set new position
            i: newId,
            y: yPosition,
          };

          // Push the new item to the dashboard items array
          state.currentDashboard.dash_items.push(newItem);
        }
      }
    },
    updateKPITitle: (
      state,
      action: PayloadAction<{ id: string; title: string }>
    ) => {
      const { id, title } = action.payload;
      // const selectedDashboard = state.dashboards.find(
      //   (dashboard) => dashboard.dash_id === state.selectedDashboardId
      // );

      const item = state.currentDashboard.dash_items.find(
        (item: any) => item.id === id
      );

      if (item) {
        item.title = title;
      }
    },
    updateKPIValue: (
      state,
      action: PayloadAction<{ id: string; value: number }>
    ) => {
      const { id, value } = action.payload;
      const item = state.currentDashboard.dash_items.find(
        (item: any) => item.id === id
      );
      if (item) {
        item.value = value;
      }
    },
  },
});

export const {
  createDashboard,
  addItem,
  selectDashboard,
  setLayout,
  setDashboards,
  deleteDashboard,
  removeItem,
  updateDashboardTitle,
  updateDashItemTitle,
  updateWidgetContent,
  updateHeading,
  updateDescription,
  updateDashInsights,
  setInitialInsights,
  addNewInsight,
  rephraseInsight,
  copyItem,
  updateKPITitle,
  updateKPIValue,
  removeDashInsight,
  setCurrentDashboard,
  setCurrentDashboardLayout,
  copyCurrentDashItem,
  resetLayout,
  removeCurrentDashItem,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
